import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useSeo } from "@/lib/seo";

const TIERS = [
  {
    key: "starter",
    name: "Starter",
    tag: "For founders & solo operators.",
    monthly: 0,
    annual: 0,
    suffix: { monthly: "Free forever · no card required", annual: "Free forever · no card required" },
    cta: "Start free",
    ctaStyle: "secondary",
    features: [
      ["1 brand", ", up to 3 competitors"],
      ["50 tracked prompts", ""],
      ["All 5 AI engines", ""],
      ["Weekly (not daily) reruns", ""],
      ["Visibility Score & share of voice", ""],
      ["Email support", ""],
    ],
  },
  {
    key: "growth",
    name: "Growth",
    tag: "For scale-up growth & marketing teams.",
    featured: true,
    monthly: 299,
    annual: 249,
    suffix: { monthly: "Billed monthly · cancel anytime", annual: "Billed annually · $2,988/yr" },
    cta: "Start 14-day trial",
    ctaStyle: "secondary",
    features: [
      ["3 brands", ", up to 12 competitors each"],
      ["500 tracked prompts", " per brand"],
      ["All 5 AI engines · ", "daily reruns"],
      ["Sentiment, accuracy, citations reports", ""],
      ["Weekly fix list with effort/lift scoring", ""],
      ["Linear, Slack, Notion, HubSpot integrations", ""],
      ["5 seats included", ""],
      ["Priority email + chat support", ""],
    ],
  },
  {
    key: "scale",
    name: "Scale",
    tag: "For enterprise & agencies.",
    monthly: 899,
    annual: 749,
    suffix: { monthly: "Billed monthly · cancel anytime", annual: "Billed annually · starts at $8,988/yr" },
    cta: "Book a demo",
    ctaStyle: "primary",
    features: [
      ["Unlimited brands", ", competitors, prompts"],
      ["Hourly reruns on priority prompts", ""],
      ["White-label client portals (agency mode)", ""],
      ["REST API + webhooks", ""],
      ["SSO (SAML, Okta, Google)", ""],
      ["SOC 2 Type II · DPA · custom contracts", ""],
      ["Unlimited seats", ""],
      ["Dedicated success manager", ""],
    ],
  },
];

const COMPARE_GROUPS = [
  {
    name: "Tracking",
    rows: [
      ["Brands", "1", "3", "Unlimited"],
      ["Competitors", "3", "12", "Unlimited"],
      ["Tracked prompts", "50", "500 per brand", "Unlimited"],
      ["Rerun frequency", "Weekly", "Daily", "Hourly (priority)"],
      ["AI engines (ChatGPT, Gemini, Perplexity, Claude, Google AIO)", "✓", "✓", "✓"],
    ],
  },
  {
    name: "Reports",
    rows: [
      ["Visibility Score & share of voice", "✓", "✓", "✓"],
      ["Citations & source authority", "—", "✓", "✓"],
      ["Sentiment & accuracy", "—", "✓", "✓"],
      ["Weekly recommendations", "—", "✓", "✓"],
    ],
  },
  {
    name: "Team & integrations",
    rows: [
      ["Seats", "1", "5 (add'l $19/mo)", "Unlimited"],
      ["Linear / Slack / Notion / HubSpot", "—", "✓", "✓"],
      ["REST API + webhooks", "—", "read-only", "✓"],
      ["White-label client portals", "—", "—", "✓"],
    ],
  },
  {
    name: "Security & support",
    rows: [
      ["SSO (SAML, Okta, Google)", "—", "Google only", "✓"],
      ["SOC 2 Type II report", "—", "✓", "✓"],
      ["Dedicated success manager", "—", "—", "✓"],
    ],
  },
];

const FAQ = [
  [
    "How do you actually get data from ChatGPT, Gemini, Claude, etc.?",
    "We use each provider's official API where one is available (OpenAI, Anthropic, Perplexity, Google) and instrumented browser sessions where it isn't (Google AI Overviews). Every answer is timestamped, attributed, and stored with its full citation list.",
  ],
  [
    "Can I cancel anytime?",
    "Yes. Monthly plans cancel monthly with no fee. Annual plans cancel at the end of the term — we don't do auto-renew traps.",
  ],
  [
    "What counts as a \"tracked prompt\"?",
    "A prompt is one buyer-style question — e.g. \"best telehealth for GLP-1.\" Each prompt is rerun across all 5 engines on the cadence of your plan. So a Growth seat with 500 prompts produces 2,500 daily answer captures.",
  ],
  [
    "Do you offer a non-profit / startup discount?",
    "Yes — 50% off Growth for the first year for pre-Series A startups and registered non-profits. Email hello@answerwatch.com.",
  ],
  [
    "What if my brand isn't in English?",
    "AnswerWatch supports 14 languages today, including Spanish, French, German, Portuguese, Italian, Dutch, Japanese, Korean, Mandarin, Hindi, and Arabic. The dashboard remains in English.",
  ],
  [
    "How do agencies typically use Scale?",
    "One workspace per client, white-labeled with your brand. You get a roll-up dashboard across all clients and per-client portals you can hand to each customer. Most agencies bill clients $400–$800/mo on top of their Scale seat.",
  ],
  [
    "Do you have an SLA?",
    "99.9% uptime on Scale, with credits if we miss. Best-effort on Starter and Growth.",
  ],
];

function Cell({ value }) {
  if (value === "✓")
    return <div className="check">✓</div>;
  if (value === "—")
    return <div style={{ color: "var(--aw-muted-2)" }}>—</div>;
  return <div>{value}</div>;
}

export default function Pricing() {
  const [annual, setAnnual] = useState(true);

  useSeo({
    title: "Pricing — AnswerWatch",
    description:
      "Track your AI visibility for less than a junior SEO. Free Starter, $249/mo Growth (annual), $749/mo Scale (annual). All plans include every engine and the weekly fix list.",
    path: "/pricing",
  });

  return (
    <div data-testid="pricing-page" style={{ background: "var(--aw-bg)" }}>
      <MarketingNav />

      {/* HERO */}
      <section style={{ padding: "80px 0 40px" }}>
        <div className="max-w-[1180px] mx-auto px-6 text-center">
          <div className="eyebrow" style={{ justifyContent: "center", marginBottom: 20 }}>
            Pricing
          </div>
          <h1 className="aw-h-display" style={{ fontSize: "clamp(48px, 7vw, 80px)" }} data-testid="pricing-h1">
            Track your AI visibility for
            <br />
            <em>less than a junior SEO.</em>
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.5, color: "var(--aw-ink-2)", maxWidth: 640, margin: "24px auto 0" }}>
            All plans include every engine, daily reruns, and the weekly fix list. Pay annually for two months
            free.
          </p>

          {/* Billing toggle */}
          <div
            className="mt-8 inline-flex p-1 rounded-full"
            style={{ background: "var(--aw-surface)", border: "1px solid var(--aw-border)" }}
            data-testid="pricing-toggle"
          >
            <button
              data-testid="pricing-toggle-monthly"
              onClick={() => setAnnual(false)}
              style={{
                height: 32,
                padding: "0 16px",
                borderRadius: 999,
                border: 0,
                background: annual ? "transparent" : "var(--aw-ink)",
                color: annual ? "var(--aw-muted)" : "var(--aw-bg)",
                fontFamily: "inherit",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Monthly
            </button>
            <button
              data-testid="pricing-toggle-annual"
              onClick={() => setAnnual(true)}
              style={{
                height: 32,
                padding: "0 16px",
                borderRadius: 999,
                border: 0,
                background: annual ? "var(--aw-ink)" : "transparent",
                color: annual ? "var(--aw-bg)" : "var(--aw-muted)",
                fontFamily: "inherit",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Annual{" "}
              <span className="mono" style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>
                −17%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* PRICING CARDS */}
      <section style={{ padding: "24px 0 96px" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TIERS.map((t) => {
              const price = annual ? t.annual : t.monthly;
              const suffix = annual ? t.suffix.annual : t.suffix.monthly;
              return (
                <div
                  key={t.key}
                  className={`price-card ${t.featured ? "featured" : ""}`}
                  data-testid={`tier-${t.key}`}
                >
                  {t.featured && (
                    <span
                      style={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "var(--aw-accent)",
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontFamily: "var(--font-mono)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Most popular
                    </span>
                  )}
                  <div>
                    <h3 style={{ fontFamily: "var(--font-display)", fontSize: 28, margin: 0 }}>{t.name}</h3>
                    <div className="muted" style={{ fontSize: 14, marginTop: 4, color: "var(--aw-muted)" }}>
                      {t.tag}
                    </div>
                  </div>
                  <div>
                    <div className="price-amt">
                      ${price}
                      {price > 0 && (
                        <span style={{ fontSize: 18, color: t.featured ? "#B8B3A6" : "var(--aw-muted)" }}>/mo</span>
                      )}
                    </div>
                    <div
                      className="price-sub"
                      style={{ fontSize: 13, marginTop: 6, color: t.featured ? "#B8B3A6" : "var(--aw-muted)" }}
                    >
                      {suffix}
                    </div>
                  </div>
                  <Link
                    to="/login"
                    data-testid={`tier-${t.key}-cta`}
                    className={`btn-aw ${t.ctaStyle === "primary" ? "btn-aw-primary" : ""}`}
                    style={
                      t.featured && t.ctaStyle === "secondary"
                        ? { background: "white", color: "var(--aw-ink)", borderColor: "white" }
                        : {}
                    }
                  >
                    {t.cta} <span className="arrow">→</span>
                  </Link>
                  <div
                    style={{
                      height: 1,
                      background: t.featured ? "rgba(255,255,255,0.1)" : "var(--aw-border)",
                    }}
                  ></div>
                  <ul>
                    {t.features.map(([bold, rest], idx) => (
                      <li key={idx}>
                        <span className="check">✓</span>
                        <span>
                          <strong style={{ fontWeight: 600 }}>{bold}</strong>
                          {rest}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <span className="pill">
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "var(--aw-accent)",
                  display: "inline-block",
                }}
              ></span>
              All plans include unlimited historical data export.
            </span>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section
        style={{
          padding: "64px 0",
          background: "var(--aw-surface-2)",
          borderTop: "1px solid var(--aw-border)",
          borderBottom: "1px solid var(--aw-border)",
        }}
      >
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center mb-10">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              Compare plans
            </div>
            <h2 className="aw-h1 mt-4">Every feature, side by side.</h2>
          </div>
          <div
            className="overflow-x-auto"
            style={{
              background: "var(--aw-surface)",
              border: "1px solid var(--aw-border)",
              borderRadius: 14,
            }}
            data-testid="pricing-compare-table"
          >
            <div style={{ minWidth: 720 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
                padding: "16px 24px",
                borderBottom: "1px solid var(--aw-border)",
                background: "var(--aw-surface-2)",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--aw-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Feature
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>Starter</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "var(--aw-accent-ink)" }}>
                Growth
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>Scale</div>
            </div>
            {COMPARE_GROUPS.map((g) => (
              <React.Fragment key={g.name}>
                <div
                  style={{
                    padding: "12px 24px",
                    background: "var(--aw-bg)",
                    borderBottom: "1px solid var(--aw-border)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "var(--aw-muted)",
                  }}
                >
                  {g.name}
                </div>
                {g.rows.map((row, i) => (
                  <div
                    key={row[0]}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
                      padding: "14px 24px",
                      borderBottom:
                        i === g.rows.length - 1 ? "none" : "1px solid var(--aw-border)",
                      fontSize: 14,
                      alignItems: "center",
                    }}
                  >
                    <div>{row[0]}</div>
                    <Cell value={row[1]} />
                    <Cell value={row[2]} />
                    <Cell value={row[3]} />
                  </div>
                ))}
              </React.Fragment>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI CALC */}
      <section style={{ padding: "96px 0" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="eyebrow mb-4">The math</div>
              <h2 className="aw-h1">
                Cheaper than the
                <br />
                one click you missed.
              </h2>
              <p style={{ fontSize: 19, lineHeight: 1.5, color: "var(--aw-ink-2)", marginTop: 20 }}>
                A single high-intent buyer your category sends to ChatGPT is worth $80–$2,400 in pipeline
                depending on what you sell. Growth costs $249 a month.
              </p>
              <p style={{ marginTop: 16, fontSize: 14.5, color: "var(--aw-muted)" }}>
                Or, put differently: AnswerWatch pays for itself the first week an AI engine starts mentioning
                you instead of a competitor.
              </p>
            </div>
            <div className="card-aw card-aw-lg" style={{ background: "var(--aw-ink)", color: "var(--aw-bg)" }}>
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  color: "#8A857A",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 14,
                }}
              >
                Worked example
              </div>
              <div className="flex flex-col gap-3.5">
                {[
                  ["Avg buyer searches/month in your category", "14,200"],
                  ["% now happening inside AI chat", "58%"],
                  ["If you appear in 1 of 4 prompts", "2,059 mentions"],
                  ["At 4% mention → trial conversion", "82 trials/mo"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between items-center pb-3.5"
                    style={{ borderBottom: "1px dashed rgba(255,255,255,0.12)" }}
                  >
                    <span style={{ color: "#C8C5BC" }}>{k}</span>
                    <span className="tnum mono" style={{ fontSize: 18 }}>
                      {v}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between items-center">
                  <span style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>Cost of Growth plan</span>
                  <span
                    className="tnum"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 28,
                      color: "oklch(0.82 0.10 155)",
                    }}
                  >
                    $249/mo
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "64px 0", borderTop: "1px solid var(--aw-border)" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
            <div className="aw-sticky">
              <div className="eyebrow mb-4">FAQ</div>
              <h2 className="aw-h1">
                Pricing,
                <br />
                contracts,
                <br />
                the boring bits.
              </h2>
            </div>
            <div>
              {FAQ.map(([q, a], i) => (
                <details
                  key={q}
                  open={i === 0}
                  style={{ borderBottom: "1px solid var(--aw-border)", padding: "20px 0" }}
                >
                  <summary
                    data-testid={`pricing-faq-${i}`}
                    style={{
                      cursor: "pointer",
                      listStyle: "none",
                      fontFamily: "var(--font-display)",
                      fontSize: 22,
                      lineHeight: 1.3,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 24,
                      alignItems: "flex-start",
                      color: "var(--aw-ink)",
                    }}
                  >
                    {q}
                    <span
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: 22,
                        color: "var(--aw-muted)",
                        flexShrink: 0,
                      }}
                    >
                      +
                    </span>
                  </summary>
                  <div
                    style={{
                      paddingTop: 12,
                      color: "var(--aw-ink-2)",
                      fontSize: 15.5,
                      lineHeight: 1.6,
                      maxWidth: 740,
                    }}
                  >
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "96px 0" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="cta-strip">
            <div>
              <div className="eyebrow" style={{ color: "oklch(0.82 0.10 155)", marginBottom: 16 }}>
                Still deciding?
              </div>
              <h2 className="aw-h1">
                Start with the free plan —
                <br />
                <em>upgrade when you see the score.</em>
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/login"
                data-testid="pricing-cta-primary"
                className="btn-aw"
                style={{
                  background: "white",
                  color: "var(--aw-ink)",
                  borderColor: "white",
                  height: 44,
                  justifyContent: "center",
                }}
              >
                Start free <span className="arrow">→</span>
              </Link>
              <Link
                to="/atlas"
                data-testid="pricing-cta-atlas"
                className="btn-aw btn-aw-ghost"
                style={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.2)",
                  height: 44,
                  justifyContent: "center",
                }}
              >
                Try the Free AI Atlas
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
